"use client";

import React, { useState, useRef, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import { Check } from "lucide-react";
import { cn } from "lib/utils";
import { useGetClients } from "modules/article/api";

interface ClientsFilterProps {
  selectedClient: string | null;
  onChange: (clientId: string | null) => void;
}

const ClientsFilter: React.FC<ClientsFilterProps> = ({
  selectedClient,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState(inputRef?.current?.value);
  const { data: clients } = useGetClients(inputRef?.current?.value);

  const clientsData = clients?.pages.flatMap((page) => page.data) || [];

  const selectedClientName = selectedClient
    ? clientsData.find((client) => client._id === selectedClient)?.name ||
      "Client inconnu"
    : "Sélectionner un client";

  const filteredClients = searchTerm
    ? clientsData.filter((client) =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : clientsData;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus the search input when opened
      if (inputRef.current) inputRef.current.focus();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" ref={popoverRef}>
      <button
        className="flex w-full items-center justify-between rounded bg-lightPrimary px-3 py-2 text-sm dark:bg-navy-900"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="truncate">{selectedClientName}</span>
        <FiSearch className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-[300px] rounded-md bg-white shadow-lg dark:bg-gray-800">
          <div className="border-b border-gray-200 p-2 dark:border-gray-700">
            <div className="flex items-center rounded bg-gray-100 px-3 py-2 dark:bg-gray-700">
              <FiSearch className="mr-2 h-4 w-4 text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Rechercher un client..."
                className="bg-transparent w-full border-none text-sm outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-auto p-1">
            {filteredClients.length === 0 && searchTerm ? (
              <div className="px-2 py-3 text-center text-sm text-gray-500">
                Aucun client trouvé.
              </div>
            ) : (
              <>
                <div
                  className="flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedClient ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span>Tous les clients</span>
                </div>

                {filteredClients.map((client) => (
                  <div
                    key={client._id}
                    className="flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      onChange(client._id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedClient === client._id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <span>{client.name}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsFilter;
